/**
 * EditableField Component
 *
 * UI/Aesthetics: Inline editable field that toggles between display and edit modes
 * Functions:
 * - Display mode: Shows value with edit button, handles empty/null values
 * - Edit mode: Shows input field with save/cancel buttons
 * - Async save operation with loading states
 * - Error handling and helper text support
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { CheckIcon, XIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

interface EditableFieldProps {
  label: string;
  value: string | null;
  onUpdate: (value: string | null) => Promise<void>;
  isLoading?: boolean;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  className?: string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  label,
  value,
  onUpdate,
  isLoading = false,
  placeholder = 'Enter value',
  helperText,
  error = false,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || '');

  const handleSave = async () => {
    await onUpdate(editValue || null);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value || '');
  };

  return (
    <div className={cn('space-y-2 mb-4', className)}>
      <Label className="text-sm font-medium text-foreground">{label}</Label>

      {isEditing ? (
        <div className="flex items-center gap-2">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            placeholder={placeholder}
            className={cn(
              'flex-1',
              error && 'border-destructive focus-visible:ring-destructive',
            )}
            disabled={isLoading}
          />
          <Button
            size="sm"
            variant="default"
            onClick={handleSave}
            disabled={isLoading}
            className="bg-green-600 hover:bg-green-700 text-white px-2"
          >
            <CheckIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-2"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground flex-1">{value || '-'}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setIsEditing(true);
              setEditValue(value || '');
            }}
            className="px-2"
          >
            <PencilSimpleIcon className="h-4 w-4" />
          </Button>
        </div>
      )}

      {helperText && (
        <p
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

export default EditableField;
